import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError, withRetry } from "../lib/api";
import { PageShell } from "../components/PageShell";
import { VragenlijstWizard } from "../components/VragenlijstWizard";
import { useLocalStorageState, hasStoredProgress } from "../lib/useLocalStorageState";
import { EMPTY_ANTWOORDEN } from "../lib/antwoordenSchema";
import { bepaalGegevensWeergave, INCOMPLEET } from "../lib/gegevensWeergave";

export default function BaselinePage() {
  const { progressie_token } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const storageKey = `motera_baseline_${progressie_token}`;
  const had = hasStoredProgress(storageKey);
  const [resumeChoice, setResumeChoice] = useState(had ? null : "fresh");
  const [antwoorden, setAntwoorden, removeAntwoorden] = useLocalStorageState(storageKey, EMPTY_ANTWOORDEN);
  const [apiError, setApiError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const versieQ = useQuery({
    queryKey: ["vragenlijst-actief"],
    queryFn: async () => (await withRetry(() => api.get("/vragenlijst/actief"))).data,
    staleTime: 5 * 60 * 1000,
  });

  const defaultsQ = useQuery({
    queryKey: ["intake-defaults", progressie_token],
    queryFn: async () => (await withRetry(() => api.get(`/werknemer/${progressie_token}/intake-defaults`))).data,
    staleTime: 60 * 60 * 1000,
  });

  // Is deze deelnemer wel compleet genoeg om te mogen invullen?
  //
  // De server houdt hem sinds blok 4 hoe dan ook tegen (422 `gegevens_niet_compleet`).
  // Dit is de vriendelijke kant daarvan: liever vooraf naar de gegevenspagina dan een
  // foutmelding na twintig ingevulde vragen.
  //
  // De beslissing komt uit `bepaalGegevensWeergave`, dezelfde functie die de
  // gegevenspagina gebruikt — hier wordt de compleetheidsregel dus niet nagerekend maar
  // gelezen. Zolang het antwoord onderweg is, is de uitkomst "laden" en gebeurt er
  // niets; daardoor kan `/gegevens` ↔ `/baseline` niet gaan pingpongen op een
  // half-geladen scherm.
  const gegevensQ = useQuery({
    queryKey: ["gegevens", progressie_token],
    queryFn: async () => (await withRetry(() => api.get(`/werknemer/${progressie_token}/gegevens`))).data,
  });
  const moetAanvullen =
    !gegevensQ.isLoading && !gegevensQ.error
    && bepaalGegevensWeergave(gegevensQ.data) === INCOMPLEET;

  useEffect(() => {
    if (moetAanvullen) navigate(`/gegevens/${progressie_token}`, { replace: true });
  }, [moetAanvullen, navigate, progressie_token]);

  // Het intake-eindpunt geeft twee verschillende 404's: geen geboortedatum, of een
  // ongeldige link. Alleen bij de eerste vraagt de wizard erom — bij een verlopen link
  // zou opslaan toch afketsen, en bij een storing bestaat de datum misschien wél en zou
  // hij overschreven worden.
  const geboortedatumOntbreekt =
    defaultsQ.error?.response?.data?.detail?.error_code === "geen_geboortedatum";

  useEffect(() => {
    if (defaultsQ.data?.leeftijd && !antwoorden.leeftijd) {
      setAntwoorden({ ...antwoorden, leeftijd: defaultsQ.data.leeftijd });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultsQ.data?.leeftijd]);

  async function submit(payload) {
    if (!versieQ.data?.versie_id) return;
    setSubmitting(true); setApiError(null);
    try {
      await withRetry(() => api.post(`/werknemer/${progressie_token}/baseline`, {
        vragenlijst_versie_id: versieQ.data.versie_id, antwoorden: payload,
      }));
      removeAntwoorden();
      navigate(`/baseline/${progressie_token}/bevestigd`);
    } catch (err) {
      const formatted = formatApiError(err, { schema: versieQ.data?.vragen_json });
      setApiError(formatted);
      if (formatted.kind === "dubbel") {
        removeAntwoorden();
        setTimeout(() => navigate(`/baseline/${progressie_token}/bevestigd?al=1`), 1500);
      }
      // De weg terug. Zonder dit leest de melding wél goed maar staat de deelnemer stil:
      // wat er ontbreekt vult hij niet hier aan maar op zijn gegevenspagina, en daar is
      // vanuit de vragenlijst geen knop naartoe. Zijn antwoorden blijven bewaard (geen
      // `removeAntwoorden` hier), zodat hij na het aanvullen verdergaat waar hij was.
      if (formatted.kind === "gegevens_niet_compleet") {
        setTimeout(() => navigate(`/gegevens/${progressie_token}`), 1500);
      }
    } finally {
      setSubmitting(false);
    }
  }

  function handleDefaultsUpdate(nieuwe) {
    if (nieuwe?.leeftijd) {
      setAntwoorden({ ...antwoorden, leeftijd: nieuwe.leeftijd });
      qc.setQueryData(["intake-defaults", progressie_token], nieuwe);
    }
  }

  // Ook wachten op de compleetheidscontrole, en de vragenlijst niet tonen aan iemand die
  // zo doorgestuurd wordt — anders ziet hij de eerste vraag nog even opflitsen.
  if (versieQ.isLoading || defaultsQ.isLoading || gegevensQ.isLoading || moetAanvullen) {
    return <PageShell><p data-testid="loading-msg" className="text-center text-sm text-slate-500">Vragenlijst laden…</p></PageShell>;
  }
  if (versieQ.error) {
    return <PageShell><p data-testid="error-load" className="text-center text-sm text-red-700">Vragenlijst kon niet geladen worden. Probeer later opnieuw.</p></PageShell>;
  }

  if (resumeChoice === null) {
    return (
      <PageShell title="Baseline-vragenlijst">
        <div data-testid="resume-prompt" className="rounded-lg border border-amber-300 bg-amber-50 p-5">
          <h2 className="text-lg font-semibold">U was al begonnen</h2>
          <p className="mt-2 text-sm text-slate-700">
            We hebben uw eerdere antwoorden bewaard op dit apparaat. Wilt u verder gaan waar u gebleven was, of opnieuw beginnen?
          </p>
          <div className="mt-4 flex gap-3">
            <button data-testid="btn-resume" onClick={() => setResumeChoice("resume")}
              className="flex-1 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700">
              Doorgaan
            </button>
            <button data-testid="btn-restart"
              onClick={() => { removeAntwoorden(); setAntwoorden(EMPTY_ANTWOORDEN); setResumeChoice("fresh"); }}
              className="flex-1 rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700">
              Opnieuw beginnen
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell title="Baseline-vragenlijst">
      <VragenlijstWizard
        initialAntwoorden={antwoorden}
        onChange={setAntwoorden}
        onSubmit={submit}
        submitting={submitting}
        apiError={apiError}
        intakeDefaults={defaultsQ.data}
        progressieToken={progressie_token}
        geboortedatumOntbreekt={geboortedatumOntbreekt}
        onDefaultsUpdate={handleDefaultsUpdate}
        schema={versieQ.data?.vragen_json}
      />
    </PageShell>
  );
}
